import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TourGuideService } from '@app/services/tour-guide.service';
import { TourGuideContent } from '@app/util/tour-guide-content.util';
import { Messages } from '@app/util/messages.util';
import { Messages as msg } from '@app/src/util/messages';
import { Labels } from '@app/src/util/labels.util';

@Component({
  selector: 'quality-gates-cnfg',
  templateUrl: './quality-gates-cnfg.component.html',
  styleUrls: ['./quality-gates-cnfg.component.scss'],
})
export class QualityGatesTableComponent implements OnInit {

  /****************** Variable declaration ******************/
  thresholdForm: FormGroup;
  flag: boolean = false;
  displayedColumns: string[] = ['position', 'name', 'description', 'threshold', 'edit',];
  saveIcon = 'save';
  AddIcon = 'Add new quality gate';
  cancelIcon = 'cancel';
  Name = Labels.name;
  Desc = Labels.desc;
  Threshold = Labels.thresHold;
  localData: any;
  tourGuideIds = TourGuideContent
  uniqueNameInput = Messages.addTool.duplicateName
  nameInput = Messages.addTool.nameInput
  emittedData: any = {
    added: [],
    updated: [],
    deleted: [],
  }
  tools: any = [];
  editingObj = '';
  getNoData = msg.no_data; //for getting no data available message

  /****************** Input and Output decorators ******************/
  @Input() dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  @Input() addtool: string;
  @Output() savedData = new EventEmitter<any>();
  @Output() deletedData = new EventEmitter<number>();
  @Output() editedData = new EventEmitter<number>();

  /**
   * constructor
   * @param formBuilder 
   * @param tourService 
   */
  constructor(private formBuilder: FormBuilder,
    public tourService: TourGuideService) {
  }

  /**ngOnInit */
  ngOnInit(): void {
    if (this.tourService.isStartTour) {
      this.initialFormValues();
      return;
    }

    this.thresholdForm = this.formBuilder.group({
      position: [],
      id: [],
      name: ['', [Validators.required, this.uniqueNameValidator(this.dataSource.data)]],
      description: ['', [Validators.required]],
      threshold: ['', [Validators.required, Validators.min(0) as any, Validators.max(9999) as any]],
      uniqId: []
    });
  }

  /**On click of save it will accept the data accordingly whether it is for edit or add */
  saveData() {
    const localData = this.dataSource.data;
    let pos = (typeof this.thresholdForm.value.position === 'number') ? this.thresholdForm.value.position : localData.length
    const updatedData = {
      id: this.thresholdForm.value.id,
      name: this.thresholdForm.value.name.trim(),
      description: this.thresholdForm.value.description.trim(),
      threshold: this.thresholdForm.value.threshold,
      position: pos,
      uniqId: this.thresholdForm.value?.uniqId || this.generateRandomNum(this.dataSource.data.filter((el) => el?.uniqId).map((el) => el.uniqId))
    };

    this.thresholdForm.patchValue({ uniqId: updatedData?.uniqId });

    localData.splice(
      pos, 1,
      this.thresholdForm.value
    );

    if (this.addtool == 'add') {
      delete this.thresholdForm.value.id
      this.tools.push(this.thresholdForm.value)
      this.savedData.emit(localData);
    }

    if (this.addtool == 'edit') {
      let objKey = updatedData.id == null ? 'added' : 'updated';

      for (let [key, value] of Object.entries(this.emittedData)) {
        this.emittedData[key] = this.emittedData[key].filter((el: any) => el.uniqId !== this.thresholdForm.value.uniqId)
      }
      this.emittedData[objKey].push(updatedData)
      this.savedData.emit(this.emittedData);
    }

    this.flag = false;
    this.dataSource.data = localData;

    // Reset input boxes
    this.resetInputData();
  }

  /**showData */
  showData() {
    this.flag = true;
    if (this.tourService.isStartTour) {
      this.initialFormValues();
      return;
    }
  }

  /**cancelData */
  cancelData() {
    this.flag = false;
    this.editingObj = ''
    this.resetInputData();
  }

  /**resetInputData */
  resetInputData() {
    this.thresholdForm.reset();
  }

  /**Custom validator function to check if the name is unique */
  uniqueNameValidator(dataSource: any[]): (control: AbstractControl) => { [key: string]: any } | null {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const name = control.value;
      const editObj: any = this.editingObj
      let idxOf = dataSource.findIndex((el: any) => el?.name == name);

      /**If the name is not provided or it's unique, return null */
      if (!name || idxOf === -1) {
        return null;
      }

      /**If the name already exists but is the current one being edited, return null */
      if (editObj?.name && idxOf !== -1 && name === editObj.name) {
        return null;
      }

      /**Name is a duplicate */
      return { duplicateName: true };
    };
  }


  //for deleting the row from table here  we used deleteRow() 
  deleteRow(i: number, element: any) {
    const deletedData = this.dataSource.data[i];
    const localData = this.dataSource.data;
    this.editingObj = ''
    if (this.addtool == 'edit') {
      for (let [key, value] of Object.entries(this.emittedData)) {
        if (key !== 'deleted') {
          this.emittedData[key] = this.emittedData[key].filter((el: any) => el?.uniqId && el.uniqId !== element?.uniqId)
        }
      }
    }

    localData.splice(i, 1);
    this.dataSource.data = localData;

    if (this.addtool == 'edit') {

      deletedData.id ? this.emittedData.deleted.push(deletedData.id) : ''; // Push the ID of the deleted quality gate

      this.savedData.emit(this.emittedData);
    }
    if (this.addtool == 'add') {
      this.savedData.emit(localData);
    }
  }

  /**For performing edit editRow() is used here. */
  editRow(i: number) {
    this.flag = true;
    const tableData = JSON.parse(JSON.stringify(this.dataSource.data[i]));

    this.editingObj = tableData;
    this.thresholdForm.patchValue({
      id: tableData.id,
      position: i,
      name: tableData.name,
      description: tableData.description,
      threshold: tableData.threshold,
      uniqId: tableData?.uniqId || this.generateRandomNum(this.dataSource.data.filter((el) => el?.uniqId).map((el) => el.uniqId))
    });
  }

  /**initial form values */
  initialFormValues() {
    this.thresholdForm = this.formBuilder.group({
      position: [0],
      id: [],
      name: ['Sonarqube', [Validators.required]],
      description: ['tool 1'],
      threshold: [50, [Validators.min(0), Validators.max(9999)]],
      uniqId: []
    });
  }

  //generate random number
  generateRandomNum(excludedNums: any) {
    let randomNum;
    do {
      randomNum = Math.floor(Math.random() * 10)
    } while (excludedNums.includes(randomNum));
    return randomNum;
  }
}
